"use client";

import { useEffect, useState, useRef } from "react";
import { EmissionEquivalentRow } from "@/types/emissions.types";
import { fetchEmissionsComparison } from "@/services/emissionsService";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { emissionsIcons } from "@/styles/results/emissionsIcons";
import { Result } from "@/types/result.types";
import { showPopup } from "../warnings/Popup";

export interface EmissionsComparisonProps {
  results: Result[];
  column: string;
  filter: string;
  onLoaded?: () => void;
}

export default function EmissionsComparison({
  results,
  column,
  filter,
  onLoaded,
}: EmissionsComparisonProps) {
  const [data, setData] = useState<EmissionEquivalentRow[]>([]);
  const [selectedCard, setSelectedCard] = useState<EmissionEquivalentRow | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasReportedLoaded = useRef(false);
  const renderedCounterRef = useRef(0);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async (emissions: number[]) => {
      try {
        const res = await fetchEmissionsComparison(column, filter, emissions);

        if (!res) {
          setData([]);
          return null;
        };
        
        setData(res);
      } catch {
        showPopup(
          "Unexpected Error",
          "An unexpected error has occured."
        )
        setData([]);
      }
    };
    if (column && filter) fetchData(results.map((v) => v.co2_kg));
  }, [column, filter, results]);

  // Called by each card when rendered
  const handleCardRender = () => {
    renderedCounterRef.current += 1;
    // Fire onLoaded only once after all cards rendered
    if (renderedCounterRef.current === data.length && onLoaded && !hasReportedLoaded.current) {
      onLoaded();
      hasReportedLoaded.current = true;
    }
  };

  const scrollByCards = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const cardWidth = container.firstElementChild
      ? (container.firstElementChild as HTMLElement).offsetWidth + 16
      : 0;
    container.scrollBy({
      left: direction === "left" ? -cardWidth * 3 : cardWidth * 3,
      behavior: "smooth",
    });
  };

  if (!data.length) return null;

  return (
    <div className="emissions-comparison-container">
      <div className="emissions-section">
        <h3 className="emissions-section-title">{filter}</h3>

        <div
          className="emissions-scroll-wrapper"
          onMouseEnter={(e) => (e.currentTarget.dataset.hovered = "true")}
          onMouseLeave={(e) => delete e.currentTarget.dataset.hovered}
        >
          <button className="scroll-arrow left" onClick={() => scrollByCards("left")}>
            <ChevronLeft size={20} />
          </button>

          <div ref={scrollRef} className="emissions-scroll-bar">
            {data.map((row, idx) => {
              const Icon =
                emissionsIcons[row.label] ||
                emissionsIcons[filter] ||
                emissionsIcons["default"];

              return (
                <EmissionCard
                  key={idx}
                  row={row}
                  Icon={Icon}
                  onClick={() => setSelectedCard(row)}
                  onRender={handleCardRender}
                />
              );
            })}
          </div>

          <button className="scroll-arrow right" onClick={() => scrollByCards("right")}>
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {selectedCard && (
        <div className="emission-popup-overlay">
          <div className="emission-popup-card">
            <div className="emission-popup-header">
              <h3 className="emission-popup-title">{selectedCard.label}</h3>
              <button
                className="close-btn emission-popup-close-btn"
                onClick={() => setSelectedCard(null)}
              >
                ✕
              </button>
            </div>
            <div className="emission-popup-body">
              <p className="emission-popup-description">{selectedCard.equivalent_description}</p>
              {selectedCard.source && (
                <p className="emission-popup-source">
                  <strong>Source:</strong> {selectedCard.source}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Single card component
const EmissionCard = ({
  row,
  Icon,
  onClick,
  onRender,
}: {
  row: EmissionEquivalentRow,
  Icon: React.ElementType;
  onClick: () => void;
  onRender: () => void;
}) => {
  const hasRendered = useRef(false);

  useEffect(() => {
    if (!hasRendered.current) {
      requestAnimationFrame(() => {
        onRender();
        hasRendered.current = true;
      });
    }
  }, [onRender]);

  return (
    <div
      className="emissions-card"
      onClick={onClick}
      role="button"
      tabIndex={0}
      data-testid={`emissions-card-${row.label}`}
    >
      <div className="emissions-card-icon-wrapper">
        {Icon && <Icon size={55} strokeWidth={1.5} color="currentColor" />}
      </div>
      <h4 className="emissions-card-title">{row.label}</h4>
      <ul className="emissions-card-values-list">
        {row.emission_equivalent_value.map((v, index) => (
          <li key={index}>
            ({index + 1}) {v.toFixed(2)} {row.emission_equivalent_unit}
          </li>
        ))}
      </ul>
    </div>
  );
};
